import {
  Button,
  FormLabel,
  Grid,
  TextField,
  createStyles,
  makeStyles,
} from "@material-ui/core";
import CustomTable, {
  HeadCell,
} from "../../../components/common/table/CustomTable";
import {
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from "@material-ui/lab";
import React, { useState } from "react";
import ReservaDetails from "./ReservaDetails";
import {
  useGetBitacoraPoQuery,
  useGetBitacoraSemanasQuery,
  useGetBitacorasQuery,
} from "../../../services/reservas/reservasService";
import { IconEye, IconFilter } from "@tabler/icons-react";
import { BitacoraMail } from "../../../interfaces/bitacoras/bitacora.interface";
import { format } from "date-fns";

const useSegmentedStyles = makeStyles((theme) =>
  createStyles({
    root: {
      border: "1px solid gainsboro",
      overflow: "hidden",
      backgroundColor: theme.palette.grey[300],
      padding: 4,

      "& .MuiButtonBase-root": {
        backgroundColor: "transparent",
        color: theme.palette.grey[600],
        border: "none",
        height: "30px",
        textTransform: "capitalize",
        "&:not(:first-child)": {
          borderRadius: theme.shape.borderRadius,
        },
        "&:first-child": {
          borderRadius: theme.shape.borderRadius,
        },
      },
      "& .Mui-selected": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
        boxShadow: "1px 1px 4px rgba(0,0,0,.3)",
      },
      "& .Mui-selected:hover": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
      },
    },
  })
);

const ReservasTable = () => {
  const classesSegmented = useSegmentedStyles();

  const [alignment, setAlignment] = React.useState<string | null>("unseen");
  const [selectedItem, setSelectedMail] = React.useState<
    BitacoraMail | undefined
  >();
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(10);

  const { data: pos, isLoading: isLoadingPos } = useGetBitacoraPoQuery();
  const { data: semanas, isLoading: isLoadingSemanas } =
    useGetBitacoraSemanasQuery();

  // Filtros consulta
  const [pickerKey, setPickerKey] = useState(1);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    po: "",
    week: "",
    date: "",
  });
  const [poFilter, setPoFilter] = useState<string | undefined>();
  const [weekFilter, setWeekFilter] = useState<string | undefined>();
  const [dateFilter, setDateFilter] = useState<string | undefined>();

  const clearFilters = () => {
    setPoFilter(undefined);
    setWeekFilter(undefined);
    setDateFilter(undefined);
    setPickerKey((val) => val + 1);
    setFilterValues({
      date: undefined,
      po: undefined,
      week: undefined,
    });
  };

  const handleClick = () => {
    setFilterValues({
      date: dateFilter,
      po: poFilter,
      week: weekFilter,
    });
  };

  const { isLoading, isFetching, data, refetch, isError } =
    useGetBitacorasQuery(
      {
        page,
        size,
        unseen: alignment === "all" ? undefined : "true",
        date: filterValues.date,
        po: filterValues.po,
        week: filterValues.week,
      },
      { refetchOnMountOrArgChange: true }
    );

  const headers: HeadCell[] = [
    {
      id: "selected",
      label: "selected",
      disablePadding: false,
      numeric: false,
      hidden: true,
    },
    { id: "number", label: "#", disablePadding: false, numeric: false },
    {
      id: "asunto",
      label: "asunto",
      disablePadding: false,
      numeric: false,
      width: "300px",
    },

    {
      id: "pendant",
      label: "pendiente revisión",
      disablePadding: false,
      numeric: false,
    },
    {
      id: "fecha",
      label: "fecha correo",
      disablePadding: false,
      numeric: false,
      width: "140px",
    },
    { id: "options", label: "Opciones", disablePadding: false, numeric: false },
  ];

  const rowsData =
    data?.data.map((bitacora, index) => ({
      selected: !bitacora.pendiente_revisar,
      number: index + 1 + page * size,
      asunto: bitacora.asunto,
      pendan: bitacora.pendiente_revisar === true ? "SI" : "NO",
      fecha:
        bitacora.fecha_envio !== undefined
          ? format(new Date(bitacora.fecha_envio), "dd/MM/yyyy - HH:mm")
          : "-",
      options: (
        <>
          <Button
            onClick={() => handleDetails(bitacora)}
            variant="outlined"
            startIcon={<IconEye />}
            size="small"
          >
            Ver
          </Button>
        </>
      ),
    })) || [];

  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    if (newAlignment !== null) {
      setAlignment(newAlignment);
    }
  };

  const handleDetails = (bitacora: BitacoraMail) => {
    setSelectedMail(bitacora);
  };

  const handleCloseDetails = () => {
    setSelectedMail(undefined);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item container spacing={2}>
          <Grid item xs={12} />
          <Grid item xs={12} sm={12} md={3}>
            <FormLabel>PO</FormLabel>
            <Autocomplete
              loading={isLoadingPos === true}
              value={poFilter ? poFilter : null}
              loadingText="Cargando..."
              options={pos || []}
              onChange={(e, val) => setPoFilter(val || undefined)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="PO" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <FormLabel>Semana</FormLabel>
            <Autocomplete
              loading={isLoadingSemanas === true}
              options={semanas || []}
              value={weekFilter ? weekFilter : null}
              loadingText="Cargando..."
              onChange={(e, val) => setWeekFilter(val || undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Semana"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <FormLabel>Fecha Correo</FormLabel>
            <TextField
              key={pickerKey}
              variant="outlined"
              fullWidth
              type="date"
              placeholder="Fecha correo"
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            style={{
              textAlign: "right",
            }}
          >
            <Button
              startIcon={<IconFilter />}
              variant="contained"
              color="secondary"
              style={{
                marginRight: 5,
              }}
              onClick={clearFilters}
            >
              Limpiar Filtros
            </Button>
            <Button
              startIcon={<IconFilter />}
              variant="contained"
              color="primary"
              onClick={handleClick}
            >
              Filtrar
            </Button>
          </Grid>
          <Grid item xs={12} />
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <ToggleButtonGroup
            className={classesSegmented.root}
            value={alignment}
            exclusive
            onChange={handleAlignment}
            aria-label="text alignment"
          >
            <ToggleButton value="unseen" aria-label="left aligned">
              No Revisados
            </ToggleButton>
            <ToggleButton value="all" aria-label="left aligned">
              Todos
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <CustomTable
            dense={true}
            isLoading={isLoading || isFetching}
            isError={isError}
            headers={headers}
            rows={rowsData}
            totalRecords={Number(data?.total)}
            page={page}
            rowsPerPage={size}
            onChangePage={(page: number) => setPage(page)}
            onChangeRowsPerPage={(rowsPerPage: number) => setSize(rowsPerPage)}
          />
        </Grid>
      </Grid>
      {selectedItem !== undefined ? (
        <ReservaDetails
          item={selectedItem}
          onClose={handleCloseDetails}
          refetch={refetch}
        />
      ) : null}
    </>
  );
};

export default ReservasTable;
